import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box } from "@caspeco/casper-ui-library.components.box";
import {
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeFontVariable,
	ThemeShadowVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalFooter,
	ModalHeader,
	useDisclosure,
} from "@caspeco/casper-ui-library.components.modal";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { useMinWidthMediaQuery } from "hooks/useMediaQuery";
import { AbortAction, ApiService } from "services/Api.service";
import { useCartAPI, useCartStore } from "store/cartStore";
import { usePlaceStore } from "store/placeStore";
import { useMenuStore } from "store/menuStore";
import { useArticleAPI } from "store/articleStore";
import { logDebug, logError } from "helpers/log-helper/log-helper";
import { CartRow } from "types/cart";
import { LogPrefix } from "types/logging";
import { BreakPoint } from "types/ui";
import { isAppWithOptionalSignIn, isAppWithRequiredSignIn } from "helpers/app-helper/app-helper";
import { useAuthAPI } from "store/authStore";
import { useBSTLUserStore } from "store/bstlUserStore";
import { getLocalStorage } from "helpers/local-storage-helper/local-storage-helper";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { Text } from "@caspeco/casper-ui-library.components.text";
import ArticleModal from "components/article-modal/ArticleModal";
import Price from "components/price/Price";
import GoToCheckoutButton from "./subcomponents/GoToCheckoutButton";
import { CartRows } from "./subcomponents/CartRows";
import { StyledDesktopCart } from "./Cart.styles";

function Cart() {
	const { placeId, menuId, appAlias } = useParams<"placeId" | "menuId" | "appAlias">();
	const { search } = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { bstlUser } = useBSTLUserStore();
	const { setIsSignInModalOpen } = useAuthAPI();
	const { menu } = useMenuStore();
	const { place } = usePlaceStore();
	const { cart, isLoadingCart, isCartModalOpen } = useCartStore();
	const { setIsCartModalOpen } = useCartAPI();
	const { getArticle } = useArticleAPI();

	const isLargerThanMediumBreakpoint = useMinWidthMediaQuery(BreakPoint.Medium);

	const [selectedCartRow, setSelectedCartRow] = useState<CartRow | undefined>();

	const {
		onClose: articleModalOnClose,
		isOpen: articleModalIsOpen,
		onOpen: articleModalOnOpen,
	} = useDisclosure({
		onClose: () => {
			// Reset selections and aborts requests directly related to open article (displayed in ArticleModal).
			ApiService.cancelRequest(AbortAction.MENU_GET_ARTICLE);
		},
	});

	const price = cart?.totalAmountExcludingTip;
	const cannotGoToCheckout =
		!cart || cart?.isEmpty() || (!menu?.open && !place?.preOrderEnabled) || isLoadingCart;

	const onClickCartRow = (cartRow: CartRow) => {
		if (!menu) return;
		setSelectedCartRow(cartRow);
		getArticle(menu.id, cartRow.article.id, cart?.cartId).catch((error) => {
			logError(
				LogPrefix.Article,
				error,
				`Could not get Article, Name: ${cartRow.article.name}, Id: ${cartRow.article.id}`,
			);
		});
		articleModalOnOpen();
	};

	const goToCheckout = () => {
		// Close the mobile cart before leaving route
		if (isCartModalOpen) setIsCartModalOpen(false);
		navigate(`/${appAlias}/${placeId}/${menuId}/checkout${search}`);
	};

	const onClickGoToCheckout = () => {
		if (isAppWithRequiredSignIn() && !bstlUser) {
			// Sign in is required and we found no signed in user,
			// prompt sign in modal, don't allow them to proceed to checkout
			logDebug(LogPrefix.Auth, `Prompting Authentication (required)`);
			setIsSignInModalOpen(true);
		} else if (isAppWithOptionalSignIn() && !bstlUser) {
			// Sign in is optional and we found no signed in user,
			// prompt sign in modal if they have not seen it before
			const userKnowsTheyCanSignIn = getLocalStorage<boolean>("hasSeenSignIn");
			if (!userKnowsTheyCanSignIn) {
				logDebug(LogPrefix.Auth, `Prompting Authentication (optional)`);
				setIsSignInModalOpen(true);
			} else {
				// User knows they can sign in but has decided not to, proceed to checkout
				goToCheckout();
			}
		} else {
			// Sign in is not available, proceed to checkout
			goToCheckout();
		}
	};

	const articleModal = (
		<ArticleModal
			isOpen={articleModalIsOpen}
			isOpenedFromCart
			onClose={articleModalOnClose}
			defaultValues={selectedCartRow}
		/>
	);

	const cartPrice =
		price && cart && !cart.isEmpty() ? (
			<Flex justifyContent="space-between" marginBottom={ThemeSpaceVariable.Medium} w="100%">
				<Text
					color={ThemeColorVariable.OnSurface}
					fontWeight="medium"
					fontFamily={ThemeFontVariable.SubHeader}
				>
					{t("money_subtotal")}
				</Text>

				<Price
					inHousePrice={price}
					color={ThemeColorVariable.OnSurface}
					fontWeight="medium"
					fontFamily={ThemeFontVariable.SubHeader}
				/>
			</Flex>
		) : (
			<></>
		);

	if (isLargerThanMediumBreakpoint) {
		return (
			<StyledDesktopCart>
				{articleModal}
				<Box pt={ThemeSpaceVariable.Large} px={ThemeSpaceVariable.Medium}>
					<Heading
						as="h2"
						fontWeight="medium"
						fontFamily={ThemeFontVariable.SubHeader}
						fontSize={ThemeFontSizeVariable.XLarge}
						color={ThemeColorVariable.OnSurfaceHeader}
					>
						{t("purchase_cart_your")}
					</Heading>
				</Box>
				<CartRows onClickCartRow={onClickCartRow} />
				{cart && !cart.isEmpty() && (
					<Box boxShadow={ThemeShadowVariable.Footer} p={ThemeSpaceVariable.Medium}>
						{cartPrice}
						<GoToCheckoutButton onClick={onClickGoToCheckout} isDisabled={cannotGoToCheckout} />
					</Box>
				)}
			</StyledDesktopCart>
		);
	}

	const onClose = () => {
		setIsCartModalOpen(false);
	};

	return (
		<>
			<Modal size="full" isOpen={isCartModalOpen} onClose={onClose}>
				<ModalHeader
					p={ThemeSpaceVariable.Medium}
					display="flex"
					justifyContent="center"
					fontFamily={ThemeFontVariable.SubHeader}
				>
					{t("purchase_cart_your")}
					<ModalCloseButton isAbsolutePositioned={true} />
				</ModalHeader>
				<ModalBody>
					{/* Opening Article modal from Cart modal requires it to be nested to be scrollable */}
					{articleModal}
					<CartRows onClickCartRow={onClickCartRow} />
				</ModalBody>
				<ModalFooter
					pt={ThemeSpaceVariable.Medium}
					px={ThemeSpaceVariable.Medium}
					pb="calc(1em + env(safe-area-inset-bottom))"
					flexDirection="column"
					boxShadow={ThemeShadowVariable.Footer}
				>
					{cartPrice}
					<GoToCheckoutButton onClick={onClickGoToCheckout} isDisabled={cannotGoToCheckout} />
				</ModalFooter>
			</Modal>
		</>
	);
}

export default Cart;
